import styles from "./Search.module.css";
import { useEffect, useState } from "react";
import { useSearchWindow } from "../../../context/SearchContext";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { useProducts } from "../../../context/ProductContext";
import SearchCard from "../../ProductCards/Search/SearchCard";
import { Package, Product } from "../../../types/product.types";
import { useOrderContext } from "../../../context/OrderContext";
import { denormalizeDate } from "../../../utils/calendar";
import CloseButton from "../../Button/CloseButton/CloseButton";
import { handleBackgroundScroll } from "../../../utils/backgroundScroll";
import { log } from "../../../utils/logger";

/**
 * Normalize string for searching:
 * - lowercases
 * - removes diacritics (č, ě, š, á, ö, …)
 */
const normalizeText = (str: string | undefined | null): string => {
    if (!str) return "";
    return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

/**
 * A popup component for searching products.
 */
const SearchPopup = () => {
    const { t } = useTranslation(); // Translation
    const { allProducts, packages, loading, reloadProducts, products } = useProducts(); // Products
    const { isOpen, close, last, setLast } = useSearchWindow(); // Search window toggle
    const { orderData } = useOrderContext();

    const [lastSearch, setLastSearch] = useState<string>(""); // Last searched term
    const [search, setSearch] = useState<string>(""); // Current searched term
    const [filteredProd, setFilteredProd] = useState<(Product | Package)[] | null>(null); // Filtered products

    // Reload products if they are loaded but don't appear
    useEffect(() => {
        if (!loading && !products) {
            reloadProducts();
        }
    }, [loading, products, reloadProducts]);

    // Search logic (case- and diacritic-insensitive)
    useEffect(() => {
        //log(search);
        const query = normalizeText(search.trim());

        if (search.length === 0) {
            setFilteredProd(null);
            setLastSearch("");
            return;
        }

        if (search.length > lastSearch.length && search.length > 1) {
            // Searching forward – filter within previous results (or products if null)
            const source = filteredProd ?? [...(products ?? [])];
            const results = source.filter((prod) => {
                const name = normalizeText(prod.name);
                const nameEn = normalizeText(prod.name_en);
                return name.includes(query) || nameEn.includes(query);
            });
            setFilteredProd(results);
        } else {
            // New search from all products
            const results = [...(products ?? [])].filter((prod) => {
                const name = normalizeText(prod.name);
                const nameEn = normalizeText(prod.name_en);
                return name.includes(query) || nameEn.includes(query);
            });
            setFilteredProd(results);
        }

        setLastSearch(search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Close cleanup / restore last search
    useEffect(() => {
        if (isOpen === false) {
            setSearch("");
            setLastSearch("");
            setFilteredProd(null);
            handleBackgroundScroll(false);
        }
        if (isOpen === true && last !== "") {
            setSearch(last);
            setLastSearch(last);
        }
    }, [isOpen, last]);

    return (
        <>
            {isOpen && (
                <div className={styles.Search}>
                    <div
                        className={styles.SearchBackground}
                        onClick={() => {
                            close();
                            setLast("");
                        }}
                    ></div>
                    <div className={styles.SearchPopup}>
                        <CloseButton
                            onClick={() => {
                                close();
                                setLast("");
                            }}
                            className={styles.ClosePopup}
                        />
                        <div className={styles.SearchPopupHeader}>
                            <p className={styles.SearchPopupTitle}>
                                {t("search_popup.title")}
                            </p>
                            <p className={styles.SearchPopupSubttle}>
                                {t("search_popup.subtitle")}{" "}
                                {denormalizeDate(orderData.selectedDate)}
                            </p>
                        </div>

                            <div className={styles.SearchCard}>
                                <div className={styles.SearchCardHeader}>
                                    <Search />
                                    <input
                                        className={styles.SearchInput}
                                        type="text"
                                        placeholder={t("search")}
                                        value={search}
                                        onChange={(event) =>
                                            setSearch(event.target.value)
                                        }
                                    />
                                </div>
                                <div className={styles.SearchCardContent}>
                                    {filteredProd &&
                                        filteredProd.map((product, i) => (
                                            <SearchCard
                                                product={product}
                                                search={search}
                                                key={i}
                                                type={product.type ?? "sales"}
                                            />
                                        ))}
                                </div>
                            </div>
                        </div>
                </div>
            )}
        </>
    );
};

export default SearchPopup;
